import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Check, X } from 'lucide-react';
import { cn } from 'helpers/cn';
import { toast } from 'sonner';
import { useForm } from 'hooks/useForm';
import { useAuth, type ChangePasswordPayload } from 'hooks/useAuth';
import { Panel, Field, FIELD_CONTROL } from '../../ConsolePanel';

export function ChangePassword() {
  const { changePassword } = useAuth();
  const { formRef, formState, isValid, reset } = useForm<PasswordForm>({}, validate);

  const newPassword = formState.newPassword ?? '';
  const confirmPassword = formState.confirmPassword ?? '';
  const confirmMismatch = confirmPassword.length > 0 && confirmPassword !== newPassword;

  const onSubmit = async () => {
    try {
      await changePassword(formState as ChangePasswordPayload);
      toast.success('Password changed. Your old password will no longer work.');
      reset();
    } catch (ex) {
      const status = (ex as { status?: number } | null)?.status;
      const description = status === 403 ? 'Your current password is incorrect.' : extractErrorMessage(ex);
      toast.error(description);
    }
  };

  /**
   * The rules list already shows which requirement a new password fails, and the
   * mismatch has its own line under the field, so the only thing the footer adds is
   * the mismatch - stated once, where the button is.
   */
  const blockingReason = confirmMismatch ? 'The two new passwords do not match' : undefined;

  return (
    <form ref={formRef} onSubmit={onSubmit}>
      <Panel
        title="Password"
        description="Changing this signs out nothing else — your other sessions stay open."
        footer={
          <>
            {!!blockingReason && <span className="supporting sm:mr-auto">{blockingReason}</span>}
            <Button type="submit" disabled={!isValid} className="h-9 rounded-lg text-body">
              Change password
            </Button>
          </>
        }
      >
        {/* Narrower than the panel: a password is a short value, and a full-width
            field invites the eye to expect a long one. */}
        <div className="grid max-w-sm gap-4">
          <Field label="Current password" htmlFor="current-password">
            <Input id="current-password" type="password" name="password" className={FIELD_CONTROL} />
          </Field>

          <Field label="New password" htmlFor="new-password">
            <Input id="new-password" type="password" name="newPassword" className={FIELD_CONTROL} />
            {newPassword.length > 0 && (
              <ul className="m-0 mt-1 list-none space-y-1 p-0">
                {PASSWORD_RULES.map((rule) => {
                  const ok = rule.test(newPassword);
                  return (
                    <li
                      key={rule.label}
                      className={cn('flex items-center gap-1.5 text-micro', ok ? 'text-ok' : 'text-dim')}
                    >
                      {ok ? <Check className="size-3.5" /> : <X className="size-3.5" />}
                      {rule.label}
                    </li>
                  );
                })}
              </ul>
            )}
          </Field>

          <Field label="Confirm new password" htmlFor="confirm-password">
            <Input id="confirm-password" type="password" name="confirmPassword" className={FIELD_CONTROL} />
            {confirmMismatch && <p className="text-danger m-0 text-micro">Passwords do not match.</p>}
          </Field>
        </div>
      </Panel>
    </form>
  );
}

type PasswordForm = {
  password: string;
  newPassword: string;
  confirmPassword: string;
};

// Mirrors the server rules in servers/api/auth/validate-password.ts
const PASSWORD_RULES: { label: string; test: (pw: string) => boolean }[] = [
  { label: 'At least 12 characters', test: (pw) => pw.length >= 12 },
  { label: 'A lowercase letter', test: (pw) => /[a-z]/.test(pw) },
  { label: 'An uppercase letter', test: (pw) => /[A-Z]/.test(pw) },
  { label: 'A number', test: (pw) => /[0-9]/.test(pw) },
  { label: 'A special character (!@#$%^&* etc.)', test: (pw) => /[!@#$%^&*()_+\-=[\]{};':"\\|,.<>/?]/.test(pw) },
];

const passwordMeetsRules = (pw: string) => PASSWORD_RULES.every((rule) => rule.test(pw));

function validate(state: Partial<PasswordForm>) {
  const { password, newPassword, confirmPassword } = state;
  return !!(
    password &&
    newPassword &&
    confirmPassword &&
    newPassword === confirmPassword &&
    passwordMeetsRules(newPassword)
  );
}

// The client throws { status, message }; message may be plain text or a JSON error body.
function extractErrorMessage(ex: unknown): string {
  const raw = (ex as { message?: string } | null)?.message ?? '';
  try {
    const parsed = JSON.parse(raw);
    return parsed.error ?? parsed.message ?? raw;
  } catch {
    return raw || 'Something went wrong, please try again later.';
  }
}
